const METHOD_PARAM_MAP: Record<string, string> = {
  'AUC/MIC': 'AUC24/MIC',
  'Target AUC': 'AUC24',
  'Target Concentration': 'Cmin',
  '%fT>MIC': '%fT>MIC',
  'Target Cmax': 'Cmax',
  'Cmax/MIC': 'Cmax/MIC',
  PTA: 'PTA',
};

export type RegimenStatusColor = 'green' | 'gold' | 'yellow' | 'orange' | 'red' | 'blue';

export interface RegimenStatus {
  label: string;
  shortLabel: string;
  color: RegimenStatusColor;
  hint?: string;
  clinicallyMet: boolean;
  baseTargetMet: boolean;
  ptaMet: boolean | null;
  ptaTarget: number | null;
  maxDoseExceeded: boolean;
  backendStatus?: string;
}

export interface EvaluationStatus {
  label: string;
  color: RegimenStatusColor;
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const normalizeNumber = (value: unknown): number | null => {
  if (isFiniteNumber(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const normalizeBoolean = (value: unknown): boolean | null => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') {
    if (value === 1) return true;
    if (value === 0) return false;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
    if (['false', '0', 'no', 'n', 'null', 'none', ''].includes(normalized)) return false;
  }
  return null;
};

const findTargetConfig = (drugConfig: any, method: string): Record<string, any> | null => {
  const targets = Array.isArray(drugConfig?.targets) ? drugConfig.targets : [];
  const matchedByMethod = targets.find((target: any) => target?.method === method);
  if (matchedByMethod) return matchedByMethod;

  const parameter = METHOD_PARAM_MAP[method];
  if (!parameter) return null;
  return targets.find((target: any) => target?.parameter === parameter) || null;
};

const hasMaxDoseError = (regimen?: any): boolean => {
  const failedRules = regimen?.safety_check?.failed_rules;
  if (Array.isArray(failedRules) && failedRules.some((rule: any) => (
    rule?.metric === 'dose.single' && rule?.severity === 'hard_block'
  ))) {
    return true;
  }
  return Array.isArray(regimen?.safety_check?.errors)
    && regimen.safety_check.errors.some((msg: unknown) => (
      String(msg).includes('安全上限')
      || String(msg).includes('限制为') && /剂量|dose/i.test(String(msg))
    ));
};

const normalizeStatusColor = (value: unknown): RegimenStatusColor => {
  if (value === 'green' || value === 'gold' || value === 'yellow' || value === 'orange' || value === 'red' || value === 'blue') {
    return value;
  }
  return 'green';
};

const BACKEND_EVALUATION_DISPLAY: Record<string, EvaluationStatus> = {
  recommended: { label: '符合方案', color: 'green' },
  cautious_recommended: { label: '谨慎推荐', color: 'orange' },
  cautious: { label: '谨慎', color: 'yellow' },
  not_recommended: { label: '未满足条件', color: 'red' },
  not_evaluable: { label: '无法评价', color: 'blue' },
  target_met: { label: '符合目标', color: 'green' },
  clinical_review: { label: '需临床复核', color: 'gold' },
  target_not_met: { label: '未满足条件', color: 'red' },
  best_feasible: { label: '需临床复核', color: 'blue' },
};

export const getPtaTarget = (selectedMethod: string, regimen?: any, drugConfig?: any): number | null => {
  const methodResult = regimen?.pkpd_results?.[selectedMethod];
  const fromResult = normalizeNumber(
    regimen?.pta_target
    ?? methodResult?.pta_target
    ?? methodResult?.details?.pta_target,
  );
  if (fromResult != null) return fromResult;

  const fromConfig = normalizeNumber(findTargetConfig(drugConfig, 'PTA')?.min);
  if (fromConfig != null) return fromConfig;

  return null;
};

export const resolveRegimenStatus = ({
  regimen,
  selectedMethod,
  drugConfig,
}: {
  regimen?: any;
  selectedMethod: string;
  drugConfig?: any;
}): RegimenStatus => {
  const primaryResult = regimen?.pkpd_results?.[selectedMethod];
  const rawPrimaryTargetMet =
    regimen?.primary_target_met ??
    primaryResult?.primary_target_met ??
    primaryResult?.details?.primary_target_met ??
    primaryResult?.details?.target_met_raw ??
    primaryResult?.target_met_raw ??
    regimen?.target_met_raw;
  const baseTargetMet = normalizeBoolean(rawPrimaryTargetMet)
    ?? normalizeBoolean(primaryResult?.target_met)
    ?? normalizeBoolean(regimen?.is_optimal)
    ?? true;
  const pta = normalizeNumber(regimen?.pta ?? primaryResult?.pta);
  const ptaTarget = getPtaTarget(selectedMethod, regimen, drugConfig);
  const explicitPtaMet = normalizeBoolean(regimen?.pta_met ?? primaryResult?.pta_met ?? primaryResult?.details?.pta_met);
  const ptaMet = explicitPtaMet ?? (pta != null && ptaTarget != null ? pta >= ptaTarget : null);
  const ptaStatus = regimen?.pta_status ?? primaryResult?.pta_status ?? primaryResult?.details?.pta_status;
  const regimenMaxDoseExceeded = normalizeBoolean(regimen?.max_dose_exceeded);
  const primaryMaxDoseExceeded = normalizeBoolean(primaryResult?.details?.max_dose_exceeded);
  const maxDoseExceeded = regimenMaxDoseExceeded === true
    || primaryMaxDoseExceeded === true
    || primaryResult?.target_status === 'max_dose_exceeded'
    || regimen?.target_status === 'max_dose_exceeded'
    || hasMaxDoseError(regimen);
  const doseMgKg = normalizeNumber(
    regimen?.dose_kg ??
    regimen?.dose_mg_kg ??
    regimen?.recommended_dose_mg_kg,
  );
  const maxDoseMgKg = normalizeNumber(
    regimen?.max_dose_mg_kg ??
    primaryResult?.details?.max_dose_mg_kg,
  );
  const backendEvaluation = regimen?.evaluation;

  if (
    backendEvaluation
    && typeof backendEvaluation === 'object'
    && typeof backendEvaluation.status === 'string'
    && typeof backendEvaluation.label === 'string'
  ) {
    const backendStatus = backendEvaluation.status;
    const fallbackStatus = BACKEND_EVALUATION_DISPLAY[backendStatus] ?? {
      label: '需临床复核',
      color: normalizeStatusColor(backendEvaluation.color),
    };
    const displayStatus = {
      label: typeof backendEvaluation.label === 'string' && backendEvaluation.label.trim()
        ? backendEvaluation.label.trim()
        : fallbackStatus.label,
      color: normalizeStatusColor(backendEvaluation.color || fallbackStatus.color),
    };
    // The backend evaluation is the single source of truth.  In particular,
    // PK-formula strategies deliberately have no PTA, so re-deriving PTA
    // locally can incorrectly turn a valid point-estimate result into a fail.
    const clinicallyMet = [
      'target_met', 'clinical_review', 'recommended', 'cautious_recommended', 'cautious',
    ].includes(backendStatus);
    const backendMaxDoseExceeded = maxDoseExceeded
      || backendEvaluation.max_dose_exceeded === true
      || (['target_not_met', 'not_recommended'].includes(backendStatus)
        && Array.isArray(backendEvaluation.reasons)
        && backendEvaluation.reasons.some((reason: unknown) => String(reason).includes('max_dose_exceeded')));

    return {
      label: displayStatus.label,
      shortLabel: displayStatus.label,
      color: displayStatus.color,
      clinicallyMet,
      baseTargetMet,
      ptaMet,
      ptaTarget,
      maxDoseExceeded: backendMaxDoseExceeded,
      backendStatus,
    };
  }

  if (maxDoseExceeded) {
    const hintParts: string[] = [];
    if (doseMgKg != null) hintParts.push(`当前剂量 ${doseMgKg.toFixed(1)} mg/kg`);
    if (maxDoseMgKg != null) hintParts.push(`超过系统上限 ${maxDoseMgKg.toFixed(1)} mg/kg`);
    if (hintParts.length === 0) hintParts.push('当前方案超过系统最大剂量上限');

    return {
      label: '未满足条件',
      shortLabel: '未满足条件',
      color: 'red',
      hint: hintParts.join('，'),
      clinicallyMet: false,
      baseTargetMet,
      ptaMet,
      ptaTarget,
      maxDoseExceeded: true,
    };
  }

  const clinicallyMet = baseTargetMet
    && ptaMet !== false
    && !(ptaTarget != null && pta == null && ptaStatus !== 'not_applicable');
  if (!clinicallyMet) {
    const hintParts: string[] = [];
    if (!baseTargetMet) {
      hintParts.push('当前方案未满足目标范围或安全限制');
    }
    if (pta != null && ptaTarget != null && ptaMet === false) {
      hintParts.push(`PTA ${pta.toFixed(1)}% 低于阈值 ${ptaTarget.toFixed(1)}%`);
    }

    return {
      label: '未满足条件',
      shortLabel: '未满足条件',
      color: 'red',
      hint: hintParts.join('，') || undefined,
      clinicallyMet: false,
      baseTargetMet,
      ptaMet,
      ptaTarget,
      maxDoseExceeded: false,
    };
  }

  return {
    label: '符合目标',
    shortLabel: '符合目标',
    color: 'green',
    hint: pta != null && ptaTarget != null
      ? `PTA ${pta.toFixed(1)}%（参考阈值 >= ${ptaTarget.toFixed(1)}%）`
      : undefined,
    clinicallyMet: true,
    baseTargetMet,
    ptaMet,
    ptaTarget,
    maxDoseExceeded: false,
  };
};

export const resolveEvaluationStatus = (
  status: RegimenStatus,
  pta?: number | null,
): EvaluationStatus => {
  if (status.backendStatus) {
    return {
      label: status.label,
      color: status.color,
    };
  }

  if (status.maxDoseExceeded || !status.clinicallyMet || status.baseTargetMet === false || status.ptaMet === false) {
    return {
      label: '未满足条件',
      color: 'red',
    };
  }

  return {
    label: '符合目标',
    color: 'green',
  };
};
